// "use client"
import { useEffect, useRef } from 'react'
import './LiquidBackground.css'

// Configuration constants
const CONFIG = {
  baseColor: '#0b0d10',
  highlight: '#4fc3f7', // light blue accent
  maxRipples: 120,
  rippleGrowth: 340, // px per second growth rate
  damping: 0.985, // amplitude decay per frame
  life: 2600, // ms
  minPointerDistance: 12, // min px movement to spawn continuous ripple
  velocityScale: 0.018,
  accelScale: 0.012,
  clickBoost: 0.45,
  resolutionScale: 0.85, // lower for perf
}

export function LiquidBackground({ className = '', enablePointerRipples = true }) {
  const canvasRef = useRef(null)
  const ripplesRef = useRef([])
  const lastPointRef = useRef(null)
  const rafRef = useRef(null)

  const combinedClassName = ['liquid-background-canvas', className].filter(Boolean).join(' ')

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let width = 0
    let height = 0

    function resize() {
      width = window.innerWidth
      height = window.innerHeight
      if (!canvas) return
      canvas.width = Math.floor(width * CONFIG.resolutionScale)
      canvas.height = Math.floor(height * CONFIG.resolutionScale)
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'
    }
    resize()
    window.addEventListener('resize', resize)

    function addRipple(x, y, strength) {
      if (ripplesRef.current.length > CONFIG.maxRipples) {
        ripplesRef.current.shift()
      }
      ripplesRef.current.push({
        x: x * CONFIG.resolutionScale,
        y: y * CONFIG.resolutionScale,
        radius: 0,
        strength,
        life: 0,
        maxLife: CONFIG.life,
      })
    }

    function pointerMove(e) {
      const now = performance.now()
      const last = lastPointRef.current
      const x = e.clientX
      const y = e.clientY
      if (last) {
        const dt = now - last.t
        if (dt > 0) {
          const dx = x - last.x
          const dy = y - last.y
          // distance moved
          const dist = Math.hypot(dx, dy)
          if (dist > CONFIG.minPointerDistance) {
            const vx = dx / dt
            const vy = dy / dt
            const speed = Math.hypot(vx, vy)
            const accel = Math.hypot(vx - last.vx, vy - last.vy)
            const strength = Math.min(1, speed * CONFIG.velocityScale + accel * CONFIG.accelScale)
            addRipple(x, y, strength)
            lastPointRef.current = { x, y, t: now, vx, vy }
          }
        }
      } else {
        lastPointRef.current = { x, y, t: now, vx: 0, vy: 0 }
      }
    }

    function pointerDown(e) {
      addRipple(e.clientX, e.clientY, CONFIG.clickBoost)
    }

    if (enablePointerRipples) {
      window.addEventListener('pointermove', pointerMove)
      window.addEventListener('pointerdown', pointerDown)
    }

    let lastFrame = performance.now()

    function render() {
      const now = performance.now()
      const dt = now - lastFrame
      lastFrame = now

      if (!canvas || !ctx) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Background gradient subtle
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
      grad.addColorStop(0, CONFIG.baseColor)
      grad.addColorStop(1, '#10151b')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ripplesRef.current.forEach(r => {
        r.life += dt
        const progress = r.life / r.maxLife
        r.radius += (CONFIG.rippleGrowth * (dt / 1000)) * (0.6 + r.strength * 0.8)
        r.strength *= CONFIG.damping
        const alpha = Math.max(0, 0.5 * (1 - progress)) * (0.4 + r.strength * 0.6)

        if (alpha <= 0.01 || progress >= 1) return

        // Draw ripple ring
        ctx.beginPath()
        ctx.arc(r.x, r.y, r.radius, 0, Math.PI * 2)
        const ringWidth = Math.max(1, 6 - progress * 5)
        const hueBlend = Math.min(1, r.strength * 2)
        // Blend highlight with accent purple for subtle color variation
        const color = `rgba(${Math.floor(79 + 48 * hueBlend)}, ${Math.floor(195 - 50 * hueBlend)}, ${Math.floor(247 - 120 * hueBlend)}, ${alpha})`
        ctx.strokeStyle = color
        ctx.lineWidth = ringWidth
        ctx.stroke()
      })

      // Remove expired
      ripplesRef.current = ripplesRef.current.filter(r => r.life < r.maxLife)

      rafRef.current = requestAnimationFrame(render)
    }
    rafRef.current = requestAnimationFrame(render)

    function visibility() {
      if (document.hidden) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
      } else {
        lastFrame = performance.now()
        rafRef.current = requestAnimationFrame(render)
      }
    }
    document.addEventListener('visibilitychange', visibility)

    return () => {
      if (enablePointerRipples) {
        window.removeEventListener('pointermove', pointerMove)
        window.removeEventListener('pointerdown', pointerDown)
      }
      document.removeEventListener('visibilitychange', visibility)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [enablePointerRipples])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={combinedClassName}
    />
  )
}
